import type { User } from './users'
import type { CommentData } from './comments'
import type { ID, UpdateData } from '../types/UtilityTypes'
import type { Tag } from './tags'
import type { TenderStatus } from '../components/data-components/pickers/TenderPicker/TenderPicker'
import type { NonEmptyArray } from 'fp-ts/es6/NonEmptyArray'
import type { ServerTask } from './tasks'
import type { FileData } from './files'
import { contramap, Ord, ordNumber } from 'fp-ts/Ord'
import { Colors } from '../styles'
import { enumMatchAll } from '../utils/enum-utils'

export enum KindType {
    DESIGN = 1,
    PRODUCTION = 2,
}

export interface ObjectType {
    id: ID,
    name: string,
    cost?: number,
    date_range?: string,
    created_at: string,
    updated_at: string,
}

export const EMPTY_OBJECT_TYPE: ObjectType = {
    id: -1,
    name: '',
    created_at: '',
    updated_at: '',
}

export interface ObjectCategory {
    id: ID,
    name: string,
}

export const EMPTY_CATEGORY: ObjectCategory = {
    id: 0,
    name: '',
}

export interface WorkObject {
    id: ID,
    name: string,
    folder?: string,
    description?: string,
    category: ObjectCategory,
    types: ObjectType[],
    tags: Tag[],
}

export const EMPTY_WORK_OBJECT: WorkObject = {
    id: 0,
    name: '',
    folder: '',
    description: '',
    category: EMPTY_CATEGORY,
    types: [],
    tags: [],
}

export enum ObjectStatus {
    OPENED = 10,
    CLOSED = 20,
}

/** @deprecated use objectToColor or objectStatusToColor instead */
export const objectStatusToClass = (status: ObjectStatus): string => 'OBJECT_STATUS_' + ObjectStatus[status]
export const objectStatusToColor = enumMatchAll<ObjectStatus>()({
    [ObjectStatus.OPENED]: Colors.blue,
    [ObjectStatus.CLOSED]: Colors.green,
})
export const objectToColor = (object: ProjectWorkObject): string =>
    (object.tasks_count === 0 || object.tasks_requested_count > 0) ?
        Colors.orange :
        objectStatusToColor(object.status_id)

export enum DesignStatus {
    NONE = 0,
    WON = 1,
    LOST = 2,
    CANCELED = 3,
}

export const designStatusToClass = (status: DesignStatus): string => 'DESIGN_STATUS_' + DesignStatus[status]
export const designStatusToText = (status: DesignStatus, isDesign: boolean): string => {
    const text = isDesign ? 'Дизайн' : 'Производство'
    const suffix = isDesign ? '' : 'о'

    return enumMatchAll<DesignStatus>()({
        [DesignStatus.NONE]: `${text} отсутствует`,
        [DesignStatus.WON]: `${text} выигран${suffix}`,
        [DesignStatus.LOST]: `${text} проигран${suffix}`,
        [DesignStatus.CANCELED]: `${text} отменен${suffix}`,
    })(status)
}

export interface ProjectWorkObject {
    id: ID,
    project_id: ID,
    deleted_at?: string,
    name?: string,
    status_id: ObjectStatus,
    files: FileData[]
    description?: string | null,
    tasks_count: number,
    tasks_closed_count: number,
    tasks_active_count: number,
    tasks_requested_count: number,
    design_status: DesignStatus,
    production_status: DesignStatus,
    work_object_type: ObjectType & { work_object: WorkObject },
    curator?: null | User,
    tags: Tag[]
}

export const EMPTY_PROJECT_WORK_OBJECT: ProjectWorkObject = {
    id: 0,
    design_status: DesignStatus.NONE,
    production_status: DesignStatus.NONE,
    project_id: 0,
    files: [],
    status_id: ObjectStatus.OPENED,
    tasks_count: 0,
    tasks_closed_count: 0,
    tasks_active_count: 0,
    tasks_requested_count: 0,
    work_object_type: {
        ...EMPTY_OBJECT_TYPE,
        work_object: EMPTY_WORK_OBJECT,
    },
    tags: [],
}

export interface FullObject extends ProjectWorkObject {
    tasks: ServerTask[],
}

export interface ObjectCreateData {
    work_object_type_id: ID,
    name?: string,
}

export type ObjectUpdateData = Pick<ProjectWorkObject, 'curator' | 'name' | 'design_status' | 'production_status' | 'status_id' | 'files' | 'description'> & {comment: CommentData}

export type ObjectUpdateRequest = UpdateData<ObjectUpdateData>

export type ObjectCloseData = {
    id: ID,
    production_status: DesignStatus,
    comment?: CommentData,
} & ({
    tasks: NonEmptyArray<{
        id: ID,
        tender_id: TenderStatus,
        tendered_user?: ID,
    }>
} | {
    design_status: DesignStatus,
    tasks: [],
})

export type ObjectRecord = Record<ID, ProjectWorkObject>

export const ordObject: Ord<ProjectWorkObject> = contramap<number, ProjectWorkObject>(object => object.tasks_count === 0? 0: object.status_id)(ordNumber)
